// ══════════════════════════════════════════════════════
//  analysis.js — AI scoreProject 输出 schema 校验器 (v2)
//
//  阶段 1 影子模式：纯 JS 校验，不引入 zod，只观察不阻断。
//  阶段 2+ 才在写边界强制校验、写 _schema_version: 2、读边界分路。
//
//  规则来源：src/services/ai.js scoreProject prompt 末尾 JSON schema。
//  违规分级：
//    error   — 会破 UI（如 scores 不在 0-10 / risks 非 string[]）
//    warning — 影响功能但可降级（如 _en 缺失 / exit_paths 空）
//    info    — 风格性缺失（如 notes.tech 缺、news_signals 子字段）
//
//  validateAnalysisV2(obj) → { ok, violations: [{path, severity, expected, actual}] }
//    ok = true 当且仅当没有 error 级违规
// ══════════════════════════════════════════════════════

export const SCHEMA_VERSION = 2;

const isString = (v) => typeof v === 'string';
const isNonEmptyString = (v) => typeof v === 'string' && v.length > 0;
const isNumber = (v) => typeof v === 'number' && !Number.isNaN(v);
const isStringArray = (v) => Array.isArray(v) && v.every(isString);
const isObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const inRange = (min, max) => (v) => isNumber(v) && v >= min && v <= max;

// 六维评分（与 ai.js scoreProject prompt 严格一致）
export const SCORE_DIMS = ['narrative', 'tech', 'team', 'tokenomics', 'data', 'compliance'];

// 三组核心 string[] trio（Q6-Q11 反复出问题的字段）
export const TRIO_FIELDS = ['catalysts', 'strengths', 'risks'];

function buildSpecs() {
  const specs = [];

  // ── 评分对象 + 六维 0-100
  // 历史 bug：v2 schema 落地时写错了量纲（0-10），实际 ai.js prompt + cache
  // 全部用 0-100。Q25（2026-06-22）修正。这之前所有 ambient/DAPPOS 等项目
  // 影子日志 FAIL 都是这个误判触发的，不是 AI 真的漏字段。
  specs.push({ path: 'scores', check: isObject, severity: 'error', expected: 'object' });
  for (const dim of SCORE_DIMS) {
    specs.push({
      path: `scores.${dim}`,
      check: inRange(0, 100),
      severity: 'error',
      expected: 'number 0-100',
    });
  }

  // ── 估值核心字段
  specs.push({ path: 'fair_value_usd', check: isNumber, severity: 'error', expected: 'number' });
  specs.push({ path: 'risk_penalty', check: inRange(0, 0.5), severity: 'warning', expected: 'number 0-0.5' });
  specs.push({ path: 'valuation_basis', check: isNonEmptyString, severity: 'warning', expected: 'non-empty string' });

  // ── 文本字段（中文 = warning，英文 = warning：双语都要在）
  for (const f of ['track', 'stage', 'tge_status', 'summary', 'recommendation', 'moat']) {
    specs.push({ path: f, check: isNonEmptyString, severity: 'warning', expected: 'non-empty string' });
    specs.push({ path: `${f}_en`, check: isNonEmptyString, severity: 'warning', expected: 'non-empty string' });
  }
  specs.push({ path: 'valuation_basis_en', check: isNonEmptyString, severity: 'warning', expected: 'non-empty string' });

  // ── 核心 string[] trio（catalysts / strengths / risks + _en）
  // 这是 Q6-Q11 的痛点字段。error 级：必须是非空 string[]
  // ai.js 已经有 placeholder 兜底，所以这里要求长度 ≥ 1 而非 ≥ 3 — 给降级路径留空间
  for (const trio of TRIO_FIELDS) {
    specs.push({
      path: trio,
      check: (v) => isStringArray(v) && v.length >= 1,
      severity: 'error',
      expected: 'non-empty string[]',
    });
    specs.push({
      path: `${trio}_en`,
      check: (v) => isStringArray(v) && v.length >= 1,
      severity: 'warning',
      expected: 'non-empty string[]',
    });
  }

  // ── exit_paths
  specs.push({
    path: 'exit_paths',
    check: (v) => isStringArray(v) && v.length >= 1,
    severity: 'warning',
    expected: 'non-empty string[]',
  });
  specs.push({
    path: 'exit_paths_en',
    check: (v) => isStringArray(v) && v.length >= 1,
    severity: 'info',
    expected: 'non-empty string[]',
  });

  // ── notes / notes_en（六维评语）
  specs.push({ path: 'notes', check: isObject, severity: 'warning', expected: 'object' });
  for (const dim of SCORE_DIMS) {
    specs.push({ path: `notes.${dim}`, check: isNonEmptyString, severity: 'info', expected: 'non-empty string' });
  }
  specs.push({ path: 'notes_en', check: isObject, severity: 'info', expected: 'object' });

  // ── news_signals（ai.js 有 _defaulted 兜底，所以这里 info 级）
  specs.push({ path: 'news_signals', check: isObject, severity: 'warning', expected: 'object' });
  specs.push({ path: 'news_signals.sentiment', check: inRange(-1, 1), severity: 'info', expected: 'number -1..1' });
  specs.push({ path: 'news_signals.red_flags', check: isStringArray, severity: 'info', expected: 'string[]' });
  specs.push({ path: 'news_signals.green_flags', check: isStringArray, severity: 'info', expected: 'string[]' });

  // ── unlock_pressure（枚举）
  specs.push({
    path: 'unlock_pressure',
    check: (v) => v === 'Green' || v === 'Yellow' || v === 'Red',
    severity: 'warning',
    expected: 'Green|Yellow|Red',
  });

  return specs;
}

export const SPECS = buildSpecs();

function getPath(obj, path) {
  return path.split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
}

function describeActual(v) {
  if (v === undefined) return 'missing';
  if (v === null) return 'null';
  if (Array.isArray(v)) {
    if (v.length === 0) return 'array[0]';
    const types = [...new Set(v.map((x) => {
      if (Array.isArray(x)) return 'array';
      if (x === null) return 'null';
      return typeof x;
    }))];
    return `array[${v.length}] of ${types.join('|')}`;
  }
  return typeof v;
}

// 主校验函数：纯函数，不抛异常，便于 shadow 模式调用
export function validateAnalysisV2(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return {
      ok: false,
      violations: [{ path: '(root)', severity: 'error', expected: 'object', actual: describeActual(obj) }],
    };
  }
  const violations = [];
  for (const spec of SPECS) {
    const v = getPath(obj, spec.path);
    if (!spec.check(v)) {
      violations.push({
        path: spec.path,
        severity: spec.severity,
        expected: spec.expected,
        actual: describeActual(v),
      });
    }
  }
  const hasErrors = violations.some((v) => v.severity === 'error');
  return { ok: !hasErrors, violations };
}

// 影子模式日志辅助：把违规摘要压缩成单行 console.warn，便于在 Railway 日志里 grep
// Q56: 旧版只详细列 error，warnings/infos 只数数量 — 看到 W6 不知道是哪 6 个。
//      改为同时列出 warning + info 路径（前 5 条），errors 保留 path=actual 形式供调试。
export function summarizeViolations(violations) {
  if (!violations || violations.length === 0) return 'clean';
  const by = { error: 0, warning: 0, info: 0 };
  for (const v of violations) by[v.severity] = (by[v.severity] || 0) + 1;
  const errs = violations.filter((v) => v.severity === 'error').slice(0, 5);
  const warns = violations.filter((v) => v.severity === 'warning').slice(0, 5);
  const infos = violations.filter((v) => v.severity === 'info').slice(0, 5);
  const errSeg  = errs.length  ? ` err:${errs.map(v => `${v.path}=${v.actual}`).join(',')}` : '';
  const warnSeg = warns.length ? ` warn:${warns.map(v => v.path).join(',')}` : '';
  const infoSeg = infos.length ? ` info:${infos.map(v => v.path).join(',')}` : '';
  return `E${by.error}/W${by.warning}/I${by.info}${errSeg}${warnSeg}${infoSeg}`;
}
